import type { AgentRun } from "./agentRuns.slice";
import type { RootState } from "Store/configureStore";

/**
 * The pending run for one agent activity, or undefined once it reported or
 * ended without a report. The run listeners read it to know a run is still
 * owed an answer; the troubleshooting panel, to know whether leaving owes the
 * user a "started" toast.
 */
export const selectAgentRun = (
  state: RootState,
  activityId: string | undefined
): AgentRun | undefined =>
  activityId ? state.agentRuns.byActivityId[activityId] : undefined;

/**
 * The run in flight for one FAILED activity, or undefined. The CTA surfaces ask
 * with the only id they hold — the failure's — to decide between offering a run
 * and reporting the one already going.
 *
 * Deliberately not a `createSelector`. It returns the stored object itself, so
 * repeated calls are reference-stable with no memoization, and the activity list
 * can ask about several expanded rows without thrashing a size-1 cache. The scan
 * is over a record that holds one entry in practice and empties itself.
 */
export const selectAgentRunBySource = (
  state: RootState,
  sourceActivityId: string | undefined
): AgentRun | undefined =>
  sourceActivityId
    ? Object.values(state.agentRuns.byActivityId).find(
        run => run.sourceActivityId === sourceActivityId
      )
    : undefined;

/**
 * The run in flight for one task on one environment, or undefined. The Logs
 * page asks before offering "Analyze": one run per task per environment, so a
 * second click cannot pay for a duplicate.
 *
 * Not a `createSelector`, for the same reasons as `selectAgentRunBySource`.
 */
export const selectAgentRunForTask = (
  state: RootState,
  {
    projectId,
    environmentId,
    taskName
  }: { projectId: string; environmentId: string; taskName: string | undefined }
): AgentRun | undefined =>
  taskName
    ? Object.values(state.agentRuns.byActivityId).find(
        run =>
          run.projectId === projectId &&
          run.environmentId === environmentId &&
          run.taskName === taskName
      )
    : undefined;
